import { useState } from "react";
import { X, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { events } from "@/lib/analytics";
import { submitLead } from "@/lib/leads";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const SERVICE_TYPES = [
  "Roof Installation",
  "Roof Repair",
  "Roof Inspection",
  "Emergency Service",
  "Storm Damage",
  "Not Sure Yet",
];

export default function EstimateModal({ open, onClose }) {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service: "",
    details: "",
  });

  if (!open) return null;

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setErrorMsg("");
    setSending(true);
    try {
      await submitLead(form, { source: "estimate_modal" });
      events.estimateFormSubmit();
      setSubmitted(true);
    } catch (err) {
      setErrorMsg(
        "We couldn't send your request right now. Please call (778) 917-6817 or email info@crestlineroofing.ca."
      );
      console.error("Lead submission failed:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="estimate-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        aria-label="Close"
        data-testid="estimate-modal-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />

      <div className="relative w-full max-w-2xl bg-[#121212] border border-white/10 max-h-[92vh] overflow-y-auto shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]">
        {/* Top amber accent */}
        <div className="h-1 bg-[var(--crest-amber)]" />

        <button
          data-testid="estimate-modal-close"
          onClick={onClose}
          className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="p-8 md:p-12">
          {!submitted ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-10 bg-[var(--crest-amber)]" />
                <span className="font-display uppercase tracking-[0.4em] text-[10px] font-semibold text-[var(--crest-amber)]">
                  Free Estimate
                </span>
              </div>
              <h3
                data-testid="estimate-modal-heading"
                className="font-display uppercase font-extrabold text-3xl md:text-4xl text-white tracking-tight leading-[0.95] mb-3"
              >
                Tell us about your roof.
              </h3>
              <p className="font-body text-sm text-white/60 mb-8 max-w-md">
                48-hour response. No pressure. No commission games. Just a clean
                quote.
              </p>

              <form onSubmit={onSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Name" id="name">
                    <Input
                      id="name"
                      data-testid="estimate-input-name"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Holloway"
                      className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-white/30 rounded-none h-12"
                    />
                  </Field>
                  <Field label="Phone" id="phone">
                    <Input
                      id="phone"
                      data-testid="estimate-input-phone"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(778) 555-0123"
                      className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-white/30 rounded-none h-12"
                    />
                  </Field>
                </div>
                <Field label="Email" id="email">
                  <Input
                    id="email"
                    data-testid="estimate-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@example.com"
                    className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-white/30 rounded-none h-12"
                  />
                </Field>
                <Field label="Property Address" id="address">
                  <Input
                    id="address"
                    data-testid="estimate-input-address"
                    required
                    value={form.address}
                    onChange={update("address")}
                    placeholder="123 Maple Street, Burnaby BC"
                    className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-white/30 rounded-none h-12"
                  />
                </Field>
                <Field label="Service Type" id="service">
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                  >
                    <SelectTrigger
                      id="service"
                      data-testid="estimate-input-service"
                      className="bg-[#1a1a1a] border-white/10 text-white rounded-none h-12 data-[placeholder]:text-white/30"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10 text-white rounded-none">
                      {SERVICE_TYPES.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`estimate-service-option-${s.toLowerCase().replace(/\s/g, "-")}`}
                          className="rounded-none focus:bg-[var(--crest-amber)] focus:text-[#121212]"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Additional details" id="details">
                  <Textarea
                    id="details"
                    data-testid="estimate-input-details"
                    rows={3}
                    value={form.details}
                    onChange={update("details")}
                    placeholder="Anything we should know — roof age, leaks, insurance claim, etc."
                    className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-white/30 rounded-none resize-none"
                  />
                </Field>

                <button
                  type="submit"
                  data-testid="estimate-submit-button"
                  disabled={sending}
                  className="w-full group inline-flex items-center justify-center gap-3 font-display uppercase tracking-wider font-bold text-base px-9 py-5 bg-[var(--crest-amber)] text-[#121212] hover:bg-[var(--crest-amber-bright)] transition-colors duration-300 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Request My Estimate
                      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                {errorMsg && (
                  <p
                    data-testid="estimate-error"
                    className="font-body text-xs text-red-300 text-center bg-red-500/10 border border-red-500/30 px-3 py-2"
                  >
                    {errorMsg}
                  </p>
                )}
                <p className="font-body text-[11px] text-white/40 text-center">
                  By submitting, you agree to be contacted by Crestline Roofing Ltd.
                  about your project.
                </p>
              </form>
            </>
          ) : (
            <div data-testid="estimate-modal-thankyou" className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[var(--crest-amber)] text-[#121212]">
                <CheckCircle2 size={32} strokeWidth={2} />
              </div>
              <h3 className="font-display uppercase font-extrabold text-3xl md:text-4xl text-white tracking-tight leading-[0.95] mb-4">
                Request received.
              </h3>
              <p className="font-body text-base text-white/65 max-w-md mx-auto leading-relaxed">
                Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}. A
                Crestline project lead will reach out within 48 hours to
                schedule your site visit. For urgent leaks or storm damage,
                call us directly.
              </p>
              <a
                href="tel:+17789176817"
                data-testid="estimate-modal-emergency-call"
                className="inline-flex items-center gap-3 mt-8 font-display uppercase tracking-wider font-bold text-sm px-7 py-4 border border-[var(--crest-amber)] text-[var(--crest-amber)] hover:bg-[var(--crest-amber)] hover:text-[#121212] transition-colors"
              >
                Call (778) 917-6817
              </a>
              <button
                onClick={onClose}
                data-testid="estimate-modal-close-confirm"
                className="block mx-auto mt-6 font-body text-xs uppercase tracking-[0.3em] text-white/40 hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="font-display uppercase tracking-[0.25em] text-[10px] font-semibold text-white/60 mb-2 block"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
